import React, { useEffect, useRef, useState } from 'react';
import './CrearProyecto.css';
import { apiFetch } from '../../../services/dashboardApi';

const API_URL = 'http://localhost:8000/api';

const buildInitialFormData = (defaults = {}) => ({
    nombre_proy: '',
    propietario: '',
    direccion: '',
    zona: '',
    id_tipo_edificacion: '',
    superficie_lote: '',
    superficie_construida: '',
    proyectista: '',
    reg_abis: defaults.reg_abis || '',
    celular: defaults.celular || '',
});

function CrearProyecto({ token, onFinalizar, onProyectoCreado }) {
    const [formData, setFormData] = useState(buildInitialFormData());
    const [socioDefaults, setSocioDefaults] = useState({ reg_abis: '', celular: '' });
    const [datosCargados, setDatosCargados] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const [archivo, setArchivo] = useState(null);
    const [descripcion, setDescripcion] = useState('');
    const [pendingDocs, setPendingDocs] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [isSavingFlow, setIsSavingFlow] = useState(false);
    const [flowPhase, setFlowPhase] = useState('idle');
    const [flowMessage, setFlowMessage] = useState('');
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [createdProject, setCreatedProject] = useState(null);

    const fileInputRef = useRef(null);

    useEffect(() => {
        const cargarDatosSocio = async () => {
            try {
                const response = await apiFetch('http://localhost:8000/api/mis-datos-socio/', {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const data = await response.json();
                if (response.ok && data.tiene_socio) {
                    const defaults = {
                        reg_abis: data.reg_abis || '',
                        celular: data.celular || '',
                    };
                    setSocioDefaults(defaults);
                    setFormData((prev) => ({ ...prev, ...defaults }));
                    setDatosCargados(true);
                }
            } catch (err) {
                console.error('Error cargando datos del socio:', err);
            }
        };

        cargarDatosSocio();
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
    };

    const handleNextFromStep1 = (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!formData.nombre_proy.trim()) {
            setError('El nombre del proyecto es obligatorio');
            return;
        }

        setCurrentStep(2);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) {
            setError('El archivo es muy grande. Máximo 50MB');
            setArchivo(null);
            e.target.value = '';
            return;
        }

        setArchivo(file);
        setError(null);
    };

    const handleAddDocToQueue = () => {
        if (!archivo) {
            setError('Selecciona un archivo para agregarlo a la lista');
            return;
        }

        const newDoc = {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            file: archivo,
            descripcion: descripcion.trim(),
        };

        setPendingDocs((prev) => [...prev, newDoc]);
        setArchivo(null);
        setDescripcion('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSuccess(`Archivo agregado a la cola: ${newDoc.file.name}`);
        setError(null);
    };

    const handleRemovePendingDoc = (docId) => {
        setPendingDocs((prev) => prev.filter((doc) => doc.id !== docId));
    };

    const handleContinueToConfirm = () => {
        if (pendingDocs.length === 0) {
            const confirmWithoutDocs = window.confirm(
                'No agregaste documentos. ¿Quieres continuar al paso de confirmación?'
            );
            if (!confirmWithoutDocs) return;
        }
        setCurrentStep(3);
    };

    const handleNuevoProyecto = () => {
        if (isSavingFlow) return;

        const confirmed = window.confirm(
            '¿Empezar un nuevo proyecto?\n\nSe limpiarán todos los datos y archivos en cola de este flujo.'
        );
        if (!confirmed) return;

        setCurrentStep(1);
        setFormData(buildInitialFormData(socioDefaults));
        setArchivo(null);
        setDescripcion('');
        setPendingDocs([]);
        setLoading(false);
        setError(null);
        setSuccess(null);
        setIsSavingFlow(false);
        setFlowPhase('idle');
        setFlowMessage('');
        setUploadProgress({ current: 0, total: 0 });
        setCreatedProject(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const finalizeCreation = async () => {
        if (isSavingFlow) return;

        if (pendingDocs.length === 0) {
            const confirmWithoutDocs = window.confirm(
                'Aún no cargaste documentos. ¿Deseas guardar el proyecto de todos modos?'
            );
            if (!confirmWithoutDocs) return;
        }

        setIsSavingFlow(true);
        setLoading(true);
        setError(null);
        setSuccess(null);
        setFlowPhase('creating');
        setFlowMessage('Creando proyecto...');

        try {
            const dataToSend = {
                nombre_proy: formData.nombre_proy.trim(),
                propietario: formData.propietario.trim() || null,
                direccion: formData.direccion.trim() || null,
                zona: formData.zona.trim() || null,
                id_tipo_edificacion: formData.id_tipo_edificacion.trim() || null,
                superficie_lote: formData.superficie_lote ? parseInt(formData.superficie_lote, 10) : null,
                superficie_construida: formData.superficie_construida ? parseInt(formData.superficie_construida, 10) : null,
                proyectista: formData.proyectista.trim() || null,
                reg_abis: formData.reg_abis.trim() || null,
                celular: formData.celular ? parseInt(formData.celular, 10) : null,
            };

            const createResponse = await apiFetch(`${API_URL}/proyectos/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(dataToSend),
            });

            const createData = await createResponse.json().catch(() => ({}));
            if (!createResponse.ok) {
                throw new Error(createData.detail || 'No se pudo crear el proyecto');
            }

            const proyecto = createData.proyecto || createData;
            setCreatedProject(proyecto);
            if (onProyectoCreado) onProyectoCreado(proyecto);

            if (pendingDocs.length > 0) {
                setFlowPhase('uploading');
                setFlowMessage('Cargando archivos...');
                setUploadProgress({ current: 0, total: pendingDocs.length });

                for (let i = 0; i < pendingDocs.length; i += 1) {
                    const doc = pendingDocs[i];
                    const form = new FormData();
                    form.append('archivo', doc.file);
                    form.append('descripcion', doc.descripcion || '');

                    const uploadResponse = await apiFetch(`${API_URL}/proyectos/${proyecto.id}/documentos/`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: form,
                    });

                    const uploadData = await uploadResponse.json().catch(() => ({}));
                    if (!uploadResponse.ok) {
                        throw new Error(uploadData.detail || `Falló la carga del archivo ${i + 1}`);
                    }

                    setUploadProgress({ current: i + 1, total: pendingDocs.length });
                }
            }

            setFlowPhase('completed');
            setFlowMessage('Creación completada');
            setSuccess('Proyecto y documentos guardados correctamente.');

            setTimeout(() => {
                if (onFinalizar) onFinalizar();
            }, 1500);
        } catch (err) {
            setFlowPhase('error');
            setFlowMessage('Ocurrió un error en el guardado');
            setError(err.message || 'Error durante la creación del proyecto');
        } finally {
            setIsSavingFlow(false);
            setLoading(false);
        }
    };

    return (
        <div className="crear-proyecto__container">
            <h2>📋 Crear Nuevo Proyecto</h2>

            <div className="wizard-steps">
                <div className={`wizard-step ${currentStep === 1 ? 'active' : currentStep > 1 ? 'done' : ''}`}>1. Datos</div>
                <div className={`wizard-step ${currentStep === 2 ? 'active' : currentStep > 2 ? 'done' : ''}`}>2. Documentos</div>
                <div className={`wizard-step ${currentStep === 3 ? 'active' : ''}`}>3. Confirmar</div>
            </div>

            {error && <div className="alert alert--error">{error}</div>}
            {success && <div className="alert alert--success">{success}</div>}

            {currentStep === 1 && (
                <>
                    <form onSubmit={handleNextFromStep1} className="crear-proyecto__form">
                        <div className="form-group">
                            <label className="form-label">Nombre del Proyecto <span className="required">*</span></label>
                            <input type="text" name="nombre_proy" value={formData.nombre_proy} onChange={handleChange} required className="form-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Propietario</label>
                            <input type="text" name="propietario" value={formData.propietario} onChange={handleChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Dirección</label>
                            <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Zona</label>
                            <input type="text" name="zona" value={formData.zona} onChange={handleChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Tipo de Edificación</label>
                            <input type="text" name="id_tipo_edificacion" value={formData.id_tipo_edificacion} onChange={handleChange} className="form-input" />
                        </div>
                        <div className="grid-2">
                            <div>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Superficie Lote (m²)</label>
                                <input type="number" name="superficie_lote" value={formData.superficie_lote} onChange={handleChange} className="form-input" />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Superficie Construida (m²)</label>
                                <input type="number" name="superficie_construida" value={formData.superficie_construida} onChange={handleChange} className="form-input" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Proyectista</label>
                            <input type="text" name="proyectista" value={formData.proyectista} onChange={handleChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Registro ABIS {datosCargados && formData.reg_abis && <span className="autocompleted">✓ Auto-completado</span>}</label>
                            <input type="text" name="reg_abis" value={formData.reg_abis} onChange={handleChange} className={`form-input ${datosCargados && formData.reg_abis ? 'form-input--autocompleted' : ''}`} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Celular {datosCargados && formData.celular && <span className="autocompleted">✓ Auto-completado</span>}</label>
                            <input type="tel" name="celular" value={formData.celular} onChange={handleChange} className={`form-input ${datosCargados && formData.celular ? 'form-input--autocompleted' : ''}`} />
                        </div>
                        <button type="submit" className="btn btn--primary">Continuar a Documentos</button>
                    </form>
                </>
            )}

            {currentStep >= 2 && (
                <div className="created-project-summary">
                    <h3>Resumen del Proyecto</h3>
                    <div className="summary-grid">
                        <div><strong>Nombre:</strong> {formData.nombre_proy || 'N/A'}</div>
                        <div><strong>Propietario:</strong> {formData.propietario || 'N/A'}</div>
                        <div><strong>Zona:</strong> {formData.zona || 'N/A'}</div>
                        <div><strong>Dirección:</strong> {formData.direccion || 'N/A'}</div>
                    </div>
                </div>
            )}

            {currentStep === 2 && (
                <div className="docs-section">
                    <h3>📄 Paso 2: Archivos en Cola</h3>

                    <div className="upload-form">
                        <div className="form-group">
                            <label className="form-label">Archivo (máx. 50MB)</label>
                            <input ref={fileInputRef} type="file" onChange={handleFileChange} disabled={loading || isSavingFlow} className="file-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Descripción (opcional)</label>
                            <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows="3" className="form-textarea" disabled={loading || isSavingFlow} />
                        </div>
                        <button type="button" className={`btn ${archivo ? 'btn--upload' : 'btn--disabled'}`} onClick={handleAddDocToQueue} disabled={!archivo || loading || isSavingFlow}>
                            Agregar archivo a la cola
                        </button>
                    </div>

                    {pendingDocs.length === 0 ? (
                        <p className="muted-message">No hay archivos en cola.</p>
                    ) : (
                        <div className="pending-docs-list">
                            <h4>Archivos listos para guardar ({pendingDocs.length})</h4>
                            {pendingDocs.map((doc) => (
                                <div key={doc.id} className="pending-doc-item">
                                    <div>
                                        <h4 className="doc-title">📄 {doc.file.name}</h4>
                                        <p className="doc-meta">📊 {formatBytes(doc.file.size)}</p>
                                        {doc.descripcion && <p className="doc-desc">{doc.descripcion}</p>}
                                    </div>
                                    <button type="button" className="btn btn--danger" onClick={() => handleRemovePendingDoc(doc.id)} disabled={loading || isSavingFlow}>
                                        Quitar
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="workflow-actions">
                        <button type="button" className="btn btn--secondary" onClick={() => setCurrentStep(1)} disabled={loading || isSavingFlow}>
                            Volver a Datos
                        </button>
                        <button type="button" className="btn btn--finalize" onClick={handleContinueToConfirm} disabled={loading || isSavingFlow}>
                            Continuar a Confirmación
                        </button>
                        <button type="button" className="btn btn--restart" onClick={handleNuevoProyecto} disabled={loading || isSavingFlow}>
                            🛑 Nuevo proyecto (Empezar de nuevo)
                        </button>
                    </div>
                </div>
            )}

            {currentStep === 3 && (
                <div className="confirm-section">
                    <h3>✅ Paso 3: Confirmar Guardado</h3>
                    <p><strong>Proyecto:</strong> {formData.nombre_proy}</p>
                    <p><strong>Archivos en cola:</strong> {pendingDocs.length}</p>

                    {flowPhase !== 'idle' && (
                        <div className={`flow-status flow-status--${flowPhase}`}>
                            <strong>{flowMessage}</strong>
                            {flowPhase === 'uploading' && (
                                <p>{uploadProgress.current} de {uploadProgress.total} archivos</p>
                            )}
                        </div>
                    )}

                    {flowPhase === 'completed' && (
                        <div className="big-check">✔</div>
                    )}

                    {createdProject?.id && (
                        <p className="doc-meta">Proyecto creado ID: {createdProject.id}</p>
                    )}

                    <div className="workflow-actions">
                        <button type="button" className="btn btn--secondary" onClick={() => setCurrentStep(2)} disabled={loading || isSavingFlow || flowPhase === 'completed'}>
                            Volver a Documentos
                        </button>
                        <button type="button" className="btn btn--finalize" onClick={finalizeCreation} disabled={loading || isSavingFlow || flowPhase === 'completed'}>
                            ✅ Finalizar creación / Guardar proyecto
                        </button>
                        <button type="button" className="btn btn--restart" onClick={handleNuevoProyecto} disabled={loading || isSavingFlow || flowPhase === 'completed'}>
                            🛑 Nuevo proyecto (Empezar de nuevo)
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CrearProyecto;
